import type { Attribute, Attribute2, Variation } from "../types";

/**
 * Hittar den rad i varianten som hör till ett givet produktattribut.
 * WC REST använder ofta pa_–slug i varianter (name: "pa_foo") medan produkten
 * använder visningsnamn ("Foo") + slug. Utan detta får kund inga
 * tillgänglighetsrutor och ingen variantmatch.
 */
function findMatchingVariationRow(
  v: Variation,
  productAttr: Attribute
): Attribute2 | undefined {
  const { name, options = [] } = productAttr;
  const slug = (productAttr as Attribute & { slug?: string }).slug;

  const byName = v.attributes.find((a) => a.name === name);
  if (byName) {
    return byName;
  }
  if (slug) {
    const bySlug = v.attributes.find(
      (a) => a.name === slug || a.slug === slug
    );
    if (bySlug) {
      return bySlug;
    }
  }
  if (name && !name.startsWith("pa_")) {
    const inferred = `pa_${name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")}`;
    const byInferred = v.attributes.find(
      (a) => a.name === inferred || a.slug === inferred
    );
    if (byInferred) {
      return byInferred;
    }
  }

  const optionCandidates = v.attributes.filter(
    (a) => a.option && options.length > 0 && options.includes(a.option)
  );
  if (optionCandidates.length === 1) {
    return optionCandidates[0];
  }
  if (optionCandidates.length > 1) {
    return optionCandidates.find(
      (a) => a.name === name || a.name === slug || a.slug === slug
    );
  }
  return undefined;
}

export function getVariationAttributeOption(
  v: Variation,
  productAttr: Attribute
): string | undefined {
  const row = findMatchingVariationRow(v, productAttr);
  if (!row) {
    return undefined;
  }
  const opt = row.option;
  if (opt === null || opt === undefined || String(opt).trim() === "") {
    return undefined;
  }
  return String(opt);
}

/**
 * Raden finns men option är tomt: WC betyder "gäller vilket värde som helst" i
 * sällsynta fall. Då accepterar vi vilket svar som listas under produkten.
 */
function variationValueMatches(
  v: Variation,
  productAttr: Attribute,
  value: string
): boolean {
  const row = findMatchingVariationRow(v, productAttr);
  if (!row) {
    return false;
  }
  const o = row.option;
  if (o === null || o === undefined || String(o).trim() === "") {
    return (productAttr.options || []).some((x) => x === value);
  }
  return String(o) === value;
}

export function partialSelectionMatches(
  v: Variation,
  focusAttribute: Attribute,
  focusOption: string,
  selected: Record<string, string>,
  required: Attribute[]
): boolean {
  if (!variationValueMatches(v, focusAttribute, focusOption)) {
    return false;
  }
  for (const attr of required) {
    if (attr.name === focusAttribute.name) {
      continue;
    }
    const selectedValue = selected[attr.name];
    if (!selectedValue) {
      continue;
    }
    if (!variationValueMatches(v, attr, selectedValue)) {
      return false;
    }
  }
  return true;
}

/**
 * Först variant som motsvarar exakt de valda attributvärdena
 * (efter samma tolkning som ovan). Köpbarkeit styrs separat via stock_status
 * (i lager / restnotering / slut).
 */
export function findMatchingProductVariation(
  selected: Record<string, string>,
  variations: Variation[],
  required: Attribute[]
): Variation | undefined {
  return variations.find((v) =>
    required.every(
      (a) => selected[a.name] && variationValueMatches(v, a, selected[a.name])
    )
  );
}
